import React, { type PropsWithChildren, type RefCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import {
	ExpandButton,
	CreateChildButtonWithExpandToggle,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/expand-button/index.tsx';
import type { mergedCell_nativeIssueTable$key } from '@atlassian/jira-relay/src/__generated__/mergedCell_nativeIssueTable.graphql';
import { useIsChildIssueCreateEnabled } from '../../../../controllers/features/selectors.tsx';

export type Props = {
	issueRef: mergedCell_nativeIssueTable$key;
	itemsConnectionId: string;
	depth: number;
	isRowHoveredOrSelected?: boolean;
	isCreateChildFormVisible?: boolean;
	createChildTriggerProps?: {
		onClick: () => void;
		ref: RefCallback<HTMLElement>;
	};
	groupId?: string;
};

export const MergedCell = ({
	children,
	issueRef,
	itemsConnectionId,
	depth,
	isRowHoveredOrSelected,
	createChildTriggerProps,
	isCreateChildFormVisible,
	groupId,
}: Props & PropsWithChildren) => {
	const issueData = useFragment<mergedCell_nativeIssueTable$key>(
		graphql`
			fragment mergedCell_nativeIssueTable on JiraIssueEdge
			@argumentDefinitions(
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isJscM2Enabled: { type: "Boolean!" }
			) {
				hasChildren(filterByProjectKeys: $projectKeys)
					@include(if: $shouldQueryHasChildren)
					@optIn(to: "JiraSpreadsheetComponent-M2")
				canHaveChildIssues(projectKey: $projectKey)
					@include(if: $shouldQueryHasChildren)
					@optIn(to: "JiraSpreadsheetComponent-M2")
				node @include(if: $isJscM2Enabled) {
					issueId
				}
			}
		`,
		issueRef,
	);

	const hasChildren = Boolean(issueData?.hasChildren);
	const canHaveChildIssues = Boolean(issueData?.canHaveChildIssues);
	const issueId = issueData?.node?.issueId;

	const isChildIssueCreateEnabled = useIsChildIssueCreateEnabled();
	const showCreateChildButton =
		isChildIssueCreateEnabled && isRowHoveredOrSelected && canHaveChildIssues;

	const childCreateButton = showCreateChildButton && issueId && (
		<Box xcss={createChildButtonStyle}>
			<CreateChildButtonWithExpandToggle
				depth={depth}
				itemId={issueId}
				groupId={groupId}
				itemsConnectionId={itemsConnectionId}
				createChildTriggerProps={createChildTriggerProps}
				isCreateChildFormVisible={isCreateChildFormVisible}
			/>
		</Box>
	);

	if (hasChildren && issueId) {
		return (
			<Box xcss={cellStyles} tabIndex={0}>
				<Box xcss={expandButtonStyle}>
					<ExpandButton itemId={issueId} itemsConnectionId={itemsConnectionId} depth={depth} />
				</Box>
				{children}
				{childCreateButton}
			</Box>
		);
	}

	return (
		<Box xcss={cellStyles} tabIndex={0}>
			{children}
			{childCreateButton}
		</Box>
	);
};

const cellStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	height: ' 100%',
	paddingLeft: 'space.100',
	position: 'relative',
});

const expandButtonStyle = xcss({
	display: 'flex',
	alignItems: 'center',
	paddingRight: 'space.100',
});

const createChildButtonStyle = xcss({
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
});
